/**
 * Generic chat block for backend-mounted interactive widgets, rendered
 * inline in the Evidence chat timeline as Heidi-native React components.
 *
 * Why native (not iframe): the original VisualDx integration plan was an
 * iframe of the vendor's hosted UI. The vendor responds with
 * ``X-Frame-Options: DENY`` (and CSP ``frame-ancestors 'none'`` on newer
 * pages), so framing is impossible. We render the form ourselves and
 * keep the vendor relationship reduced to MCP server-to-server calls.
 *
 * Why generic: the SSE chunk type is ``interactive_widget`` (not
 * ``visualdx_widget``) and dispatch happens via {@link WIDGET_REGISTRY}
 * keyed on a ``kind`` discriminator. Adding a new widget kind (pediatric
 * dose calc, pre-op risk score, imaging order builder, …) is a one-entry
 * change in the registry plus a matching ``InteractiveWidgetKind`` value;
 * everything in ChatTimeline + the SSE processor stays vendor-agnostic.
 */

import {
  TypographyCaption,
  TypographyP2,
} from '@heidi/design-system/typography';
import {
  type InteractiveWidgetSubmission,
  VisualDxDiagnosticFlow,
  type VisualDxDiagnosticFlowSelections,
  VisualDxCompare,
  VisualDxDrugReaction,
  VisualDxGallery,
  VisualDxRankedDifferential,
} from '@heidi/evidence';
import { cn } from '@/lib/cn';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { AlertCircle, Check, Eye, Loader2 } from 'lucide-react';
import type {
  InteractiveWidgetKind,
  VisualDxComparePayload,
  VisualDxDrugReactionPayload,
  VisualDxGalleryPayload,
  VisualDxRankedDifferentialEntry,
} from '@heidi/evidence/contracts';

/**
 * Props the chat passes per-widget into ``WidgetConfig.renderInline``.
 *
 * Kept as a single bag so the registry is variance-free (one
 * ``renderInline`` shape regardless of widget kind). Per-widget
 * renderers ignore the fields that don't apply to them.
 */
interface WidgetRenderProps {
  /** Hands the widget's typed selection back to the chat for dispatch. */
  onSubmit: (submission: InteractiveWidgetSubmission) => void;
  /**
   * Present for the ``visualdx_ranked_differential`` kind only — the
   * BE-enriched ranked-list payload. Other widget kinds ignore it.
   */
  differential?: VisualDxRankedDifferentialEntry[];
  /** Present for the ``visualdx_gallery`` kind only. */
  gallery?: VisualDxGalleryPayload;
  /** Present for the ``visualdx_compare`` kind only. */
  compare?: VisualDxComparePayload;
  /** Present for the ``visualdx_drug_ddx`` kind only. */
  drugReaction?: VisualDxDrugReactionPayload;
}

/**
 * Per-``kind`` rendering config for the generic interactive widget block.
 *
 * Adding a new widget kind = add a new entry here (and a matching ``kind``
 * value in ``InteractiveWidgetKind``). The block stays kind-agnostic — it
 * only reads from this registry, so the call sites in ChatTimeline and the
 * SSE processor never need to learn about any specific widget.
 */
interface WidgetConfig {
  loadingLabelKey: TranslationKey;
  loadingLabelWithQueryKey: TranslationKey;
  completedLabelKey: TranslationKey;
  renderInline: (props: WidgetRenderProps) => JSX.Element;
}

const WIDGET_REGISTRY: Record<InteractiveWidgetKind, WidgetConfig> = {
  visualdx_diagnostic_flow: {
    loadingLabelKey: 'chat.visualdxLookingUp',
    loadingLabelWithQueryKey: 'chat.visualdxLookingUpWithQuery',
    completedLabelKey: 'chat.visualdxLoaded',
    renderInline: ({ onSubmit }) => (
      <VisualDxDiagnosticFlow
        onSubmit={(selections: VisualDxDiagnosticFlowSelections) =>
          onSubmit({ kind: 'visualdx_diagnostic_flow', selections })
        }
      />
    ),
  },
  visualdx_ranked_differential: {
    loadingLabelKey: 'chat.visualdxLookingUp',
    loadingLabelWithQueryKey: 'chat.visualdxLookingUp',
    completedLabelKey: 'chat.visualdxLoaded',
    renderInline: ({ differential }) => (
      <VisualDxRankedDifferential differential={differential ?? []} />
    ),
  },
  visualdx_gallery: {
    loadingLabelKey: 'chat.visualdxLookingUp',
    loadingLabelWithQueryKey: 'chat.visualdxLookingUpWithQuery',
    completedLabelKey: 'chat.visualdxLoaded',
    renderInline: ({ gallery }) =>
      gallery ? <VisualDxGallery gallery={gallery} /> : <></>,
  },
  visualdx_compare: {
    loadingLabelKey: 'chat.visualdxLookingUp',
    loadingLabelWithQueryKey: 'chat.visualdxLookingUpWithQuery',
    completedLabelKey: 'chat.visualdxLoaded',
    renderInline: ({ compare }) =>
      compare ? <VisualDxCompare compare={compare} /> : <></>,
  },
  visualdx_drug_ddx: {
    loadingLabelKey: 'chat.visualdxLookingUp',
    loadingLabelWithQueryKey: 'chat.visualdxLookingUpWithQuery',
    completedLabelKey: 'chat.visualdxLoaded',
    renderInline: ({ drugReaction }) =>
      drugReaction ? <VisualDxDrugReaction drugReaction={drugReaction} /> : <></>,
  },
};

interface InteractiveWidgetBlockProps {
  kind: InteractiveWidgetKind;
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  error?: string;
  /** Optional payload for kinds that need server-derived data. */
  differential?: VisualDxRankedDifferentialEntry[];
  /** Optional payload for the ``visualdx_gallery`` kind. */
  gallery?: VisualDxGalleryPayload;
  /** Optional payload for the ``visualdx_compare`` kind. */
  compare?: VisualDxComparePayload;
  /** Optional payload for the ``visualdx_drug_ddx`` kind. */
  drugReaction?: VisualDxDrugReactionPayload;
  /**
   * Invoked once when the inline widget signals submission. The chat
   * plumbing (Chat.tsx) is expected to format the payload and dispatch
   * it as the next user turn.
   */
  onSubmit?: (submission: InteractiveWidgetSubmission) => void;
  className?: string;
}

/**
 * Generic chat block that renders a backend-mounted interactive widget
 * inline as a Heidi-native React component.
 *
 * The block dispatches on ``kind`` via {@link WIDGET_REGISTRY}; everything
 * vendor-specific (labels, renderer) lives in that map. The block itself
 * only knows about loading / error / completed lifecycle and the standard
 * chat-card chrome.
 */
export const InteractiveWidgetBlock = ({
  kind,
  status,
  query,
  error,
  differential,
  gallery,
  compare,
  drugReaction,
  onSubmit,
  className,
}: InteractiveWidgetBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const config = WIDGET_REGISTRY[kind];

  const fetchingLabel = query
    ? t(config.loadingLabelWithQueryKey, { query })
    : t(config.loadingLabelKey);

  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            <TypographyP2 className="min-w-0 truncate text-text-secondary">
              {fetchingLabel}
            </TypographyP2>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'w-full min-w-0 overflow-hidden rounded-lg border border-validation-destructive-border bg-validation-destructive-muted duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex items-center gap-2 p-3">
          <AlertCircle className="size-4 shrink-0 text-validation-destructive" />
          <TypographyP2 className="w-0 flex-1 truncate text-validation-destructive">
            {error || t('chat.visualdxError')}
          </TypographyP2>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border border-border duration-300 animate-in fade-in',
        className
      )}
    >
      <div className="flex items-center gap-2 p-3">
        <Eye className="size-4 shrink-0 text-text-secondary" />
        <TypographyP2 className="w-0 flex-1 truncate text-text-secondary">
          {t(config.completedLabelKey)}
        </TypographyP2>
        <TypographyCaption className="flex shrink-0 items-center gap-1 whitespace-nowrap text-text-tertiary duration-200 animate-in fade-in">
          <Check className="size-3.5" />
          {t('chat.completed')}
        </TypographyCaption>
      </div>

      <div className="border-t border-border p-3 pt-2">
        {config.renderInline({
          onSubmit: (submission) => onSubmit?.(submission),
          differential,
          gallery,
          compare,
          drugReaction,
        })}
      </div>
    </div>
  );
};
